import { isArray, isEmpty, isEqual, isNil, isNumber } from 'lodash';
import { useMemo } from 'react';
import { User } from '@shared/types/user';
import { useListUsersById } from '@shared/util/hooks';
import { EditableField, Field } from './Field';

export const CURRENT_USER_TOKEN = '{me}';

export class PersonField implements EditableField {
  readonly fieldType = 'PersonField';
  readonly userIds: number[];
  readonly users: User[];

  constructor(userIds: number[], personRefs: User[] | null) {
    this.userIds = userIds;
    this.users = personRefs?.filter(user => userIds.includes(user.id)) ?? [];
  }

  validate(): string | null {
    return null;
  }

  isEqual(field: Field): boolean {
    return field instanceof PersonField && isEqual(this.userIds, field.userIds);
  }

  toFieldJson() {
    return this.userIds;
  }

  toDisplayString(): string {
    // only hydrated refs have names; unhydrated ids contribute nothing
    return this.users.map(user => user.fullName).join(', ');
  }

  get isEmpty() {
    return isEmpty(this.userIds);
  }

  static fromFieldJson(
    jsonBlob: any,
    personRefs: User[] | null,
    currentUser?: User,
  ): PersonField | null {
    if (jsonBlob === CURRENT_USER_TOKEN && !isNil(currentUser)) {
      return new PersonField([currentUser.id], personRefs);
    } else if (isArray(jsonBlob) && jsonBlob.every(isNumber)) {
      return new PersonField(jsonBlob, personRefs);
    } else {
      return null;
    }
  }
}

export function usePersons(field: PersonField | null) {
  const existingUsers = useMemo(() => field?.users ?? [], [field]);
  const missingUserIds = useMemo(() => {
    const existingUserIds = existingUsers.map(user => user.id);
    return field?.userIds.filter(id => !existingUserIds.includes(id)) ?? [];
  }, [field, existingUsers]);
  const { users, loading } = useListUsersById(missingUserIds);
  const persons = useMemo(() => [...existingUsers, ...users], [existingUsers, users]);
  return { loading, persons };
}
